// Dashboard: hybrid layout (Variant C) — data (bar chart + per-dag completed panels)
// links, progress/reflection rechts in side rail. Zie plan-dashboard-src-prototype-align.md.
// Phase 5: Glance-stack on Battlefield, bars vary by breakpoint, rail desktop-only.
import { useState, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useDeviceClass } from '@/hooks/ui/useBreakpoint';
import { useT } from '@/i18n/useT';
import { PANEL_GAP } from '@/components/layout/spacingTokens';
import { useTasksContext } from '@/features/tasks';
import { useDelegatedTasks } from '@/features/dashboard/hooks/use-delegated-tasks';
import {
  useWeeklyCompletion,
  getVisibleBarDays,
} from '@/features/dashboard/hooks/use-weekly-completion';
import { useWeeklyStats } from '@/features/dashboard/hooks/use-weekly-stats';
import DashboardSideRail from '@/features/dashboard/components/DashboardSideRail';
import { DashboardGlanceStats } from '@/features/dashboard/components/DashboardGlanceStats';
import { DashboardChartSection } from '@/features/dashboard/components/DashboardChartSection';
import { DashboardListSection } from '@/features/dashboard/components/DashboardListSection';
import { DashboardMainColumn } from '@/features/dashboard/components/DashboardMainColumn';
import { countCompletedTabBadge } from '@/features/dashboard/utils/dashboardListFilters';
import {
  PAGE_PADDING_BATTLEFIELD,
  PAGE_PADDING_DESKTOP,
  countWaitingTasks,
  aggregateProjectHours,
} from '@/features/dashboard/utils/dashboardPageUtils';
import { useDashboardTabs } from '@/features/dashboard/hooks/use-dashboard-tabs';
import {
  getFreeHistoryCutoffDateStr,
} from '@/features/dashboard/utils/dashboardHistoryLimit';
import { useTier } from '@/features/tier';
import { WallModal } from '@/features/wall';
import { useDashboardHistoryWall } from '@/features/dashboard/hooks/use-dashboard-history-wall';

const DashboardPage = () => {
  const t = useT();
  const [dashTab, setDashTab] = useState(0);
  const [openDay, setOpenDay] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [projectFilter, setProjectFilter] = useState(null);
  const [searchParams] = useSearchParams();
  const { breakpoint, isBattlefield, isDesktop } = useDeviceClass();
  const { isFree } = useTier();

  const { filteredTasks, projectDisplayMap } = useTasksContext();
  const filteredTaskIds = useMemo(
    () => new Set((filteredTasks ?? []).map((task) => task.id)),
    [filteredTasks],
  );

  const delegatedTasksByPerson = useDelegatedTasks();
  const waitingCount = useMemo(
    () => countWaitingTasks(delegatedTasksByPerson),
    [delegatedTasksByPerson],
  );
  const historyCutoffDateStr = useMemo(
    () => (isFree ? getFreeHistoryCutoffDateStr(new Date()) : null),
    [isFree],
  );
  const {
    barData: allBarData,
    completedDays,
    canGoBack,
    canGoForward,
  } = useWeeklyCompletion(weekOffset, { historyCutoffDateStr });
  const completedCount = useMemo(
    () =>
      countCompletedTabBadge(completedDays, {
        breakdownProjectFilter: projectFilter,
        filteredTaskIds,
        projectDisplayMap,
      }),
    [completedDays, projectFilter, filteredTaskIds, projectDisplayMap],
  );
  const { totalHours, totalTasks, activeDays, delta, bestDay, biggestTask } =
    useWeeklyStats(completedDays);

  const { dashTabs, activeTabId, completedTabIndex } = useDashboardTabs({
    isBattlefield,
    t,
    waitingCount,
    completedCount,
    dashTab,
    setDashTab,
    searchParams,
  });

  const visibleBarDays = getVisibleBarDays(breakpoint);
  const barData = allBarData.slice(-visibleBarDays);

  const projectDataAll = aggregateProjectHours(barData);
  const projectData = isBattlefield
    ? projectDataAll.sort((a, b) => b.hours - a.hours).slice(0, 3)
    : projectDataAll;

  const handleDayClick = (dateStr) => {
    const idx = completedDays.findIndex((d) => d.dateStr === dateStr);
    if (idx === -1) return;
    setProjectFilter(null);
    if (completedTabIndex >= 0) setDashTab(completedTabIndex);
    setOpenDay(idx);
  };

  const handleProjectClick = (name) => {
    setProjectFilter((prev) => (prev === name ? null : name));
    if (completedTabIndex >= 0) setDashTab(completedTabIndex);
  };

  const { historyWallOpen, closeHistoryWall, handlePreviousWeek } =
    useDashboardHistoryWall({
      isFree,
      setWeekOffset,
      setOpenDay,
      setProjectFilter,
    });

  const handleNextWeek = () => {
    setWeekOffset((offset) => Math.max(0, offset - 1));
    setOpenDay(0);
    setProjectFilter(null);
  };

  const pagePadding = isBattlefield
    ? PAGE_PADDING_BATTLEFIELD
    : PAGE_PADDING_DESKTOP;

  return (
    <Box
      sx={{
        p: pagePadding,
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: PANEL_GAP,
      }}
    >
      {isBattlefield ? (
        <DashboardGlanceStats
          breakpoint={breakpoint}
          totalHours={totalHours}
          totalTasks={totalTasks}
          activeDays={activeDays}
          delta={delta}
          bestDay={bestDay}
          biggestTask={biggestTask}
        />
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexDirection: isDesktop ? 'row' : 'column',
          gap: PANEL_GAP,
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <DashboardMainColumn
          isBattlefield={isBattlefield}
          panelGap={PANEL_GAP}
          chartSection={
            <DashboardChartSection
              barData={barData}
              onDayClick={handleDayClick}
              canGoBack={canGoBack}
              canGoForward={canGoForward}
              onPreviousWeek={handlePreviousWeek}
              onNextWeek={handleNextWeek}
              projectData={projectData}
              projectFilter={projectFilter}
              onProjectClick={handleProjectClick}
            />
          }
          listSection={
            <DashboardListSection
              dashTabs={dashTabs}
              dashTab={dashTab}
              onDashTabChange={(_, v) => setDashTab(v)}
              activeTabId={activeTabId}
              delegatedTasksByPerson={delegatedTasksByPerson}
              completedDays={completedDays}
              openDay={openDay}
              setOpenDay={setOpenDay}
              projectFilter={projectFilter}
            />
          }
        />

        {isDesktop ? (
          <DashboardSideRail
            totalHours={totalHours}
            totalTasks={totalTasks}
            delta={delta}
            activeDays={activeDays}
            bestDay={bestDay}
            biggestTask={biggestTask}
          />
        ) : null}
      </Box>

      <WallModal
        open={historyWallOpen}
        onClose={closeHistoryWall}
        wallType="dashboard_scroll"
      />
    </Box>
  );
};

export default DashboardPage;
